import { useMemo, useState } from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@rudi/ui/table';
import { Badge } from '@rudi/ui/badge';
import { Button } from '@rudi/ui/button';
import { Input } from '@rudi/ui/input';
import { Label } from '@rudi/ui/label';
import { Switch } from '@rudi/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@rudi/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@rudi/ui/dialog';
import { toast } from 'sonner';
import { PageHeader, QueryState } from '../components/page';
import {
  useClearPlatformOverride,
  useDeleteFeatureOverride,
  useFeatureOverrides,
  useFeatures,
  useSetPlatformOverride,
  useTenants,
  useUpsertFeatureOverride,
  useUsers,
} from '../api/hooks';
import type { FeatureRegistryEntry, FeatureRoleOption } from '../api/types';

type EnvironmentState = 'default' | 'on' | 'off';

function environmentState(entry: FeatureRegistryEntry): EnvironmentState {
  if (entry.platform_override === null) return 'default';
  return entry.platform_override ? 'on' : 'off';
}

function AddOverrideForm({
  featureKey,
  roles,
}: {
  featureKey: string;
  roles: FeatureRoleOption[];
}) {
  const [subjectType, setSubjectType] = useState<'user' | 'tenant' | 'role'>('tenant');
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [enabled, setEnabled] = useState(true);
  const { data: usersData } = useUsers();
  const { data: tenantsData } = useTenants();
  const upsert = useUpsertFeatureOverride();

  const candidates = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (subjectType === 'user') {
      const users = usersData?.users ?? [];
      const filtered = needle
        ? users.filter(u =>
            [u.email, u.username, u.display_name, u.tenant_name]
              .filter(Boolean)
              .some(v => v!.toLowerCase().includes(needle))
          )
        : users;
      return filtered.slice(0, 6).map(u => ({
        id: u.id,
        label: u.email ?? u.display_name ?? u.username,
        detail: u.tenant_name ?? undefined,
      }));
    }
    if (subjectType === 'tenant') {
      const tenants = tenantsData?.tenants ?? [];
      const filtered = needle
        ? tenants.filter(t => t.name.toLowerCase().includes(needle))
        : tenants;
      return filtered.slice(0, 6).map(t => ({
        id: t.id,
        label: t.name,
        detail: `${t.member_count} members`,
      }));
    }
    const filtered = needle
      ? roles.filter(role =>
          [role.display_name, role.name, role.tenant_name].some(value =>
            value.toLowerCase().includes(needle)
          )
        )
      : roles;
    return filtered.slice(0, 6).map(role => ({
      id: role.id,
      label: role.display_name,
      detail: role.tenant_name,
    }));
  }, [subjectType, search, usersData, tenantsData, roles]);

  const selected = candidates.find(c => c.id === selectedId) ?? null;

  const onAdd = async () => {
    if (!selectedId) return;
    try {
      await upsert.mutateAsync({
        key: featureKey,
        subject_type: subjectType,
        subject_id: selectedId,
        enabled,
      });
      toast.success('Override saved');
      setSelectedId(null);
      setSearch('');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Save failed');
    }
  };

  return (
    <div className="space-y-2 rounded-md border p-3">
      <Label className="block">Add override</Label>
      <div className="flex gap-2">
        <Select
          value={subjectType}
          onValueChange={v => {
            setSubjectType(v as 'user' | 'tenant' | 'role');
            setSelectedId(null);
            setSearch('');
          }}
        >
          <SelectTrigger className="w-28">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="user">user</SelectItem>
            <SelectItem value="tenant">tenant</SelectItem>
            <SelectItem value="role">role</SelectItem>
          </SelectContent>
        </Select>
        <Input
          placeholder={subjectType === 'user' ? 'Search by email…' : 'Search by name…'}
          value={selected ? selected.label : search}
          onChange={e => {
            setSelectedId(null);
            setSearch(e.target.value);
          }}
        />
        <div className="flex items-center gap-2">
          <Switch checked={enabled} onCheckedChange={setEnabled} />
          <span className="text-xs text-muted-foreground">{enabled ? 'ON' : 'OFF'}</span>
        </div>
        <Button onClick={onAdd} disabled={!selectedId || upsert.isPending}>
          Add
        </Button>
      </div>
      {!selected && search.trim() && (
        <div className="overflow-hidden rounded-md border">
          {candidates.length === 0 && (
            <p className="px-3 py-2 text-sm text-muted-foreground">No matches.</p>
          )}
          {candidates.map(c => (
            <button
              key={c.id}
              className="flex w-full items-center justify-between px-3 py-1.5 text-left text-sm hover:bg-muted"
              onClick={() => setSelectedId(c.id)}
            >
              <span>{c.label}</span>
              {c.detail && <span className="text-xs text-muted-foreground">{c.detail}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function FeatureDetailDialog({
  feature,
  roles,
  onClose,
}: {
  feature: FeatureRegistryEntry;
  roles: FeatureRoleOption[];
  onClose: () => void;
}) {
  const { data } = useFeatureOverrides(feature.key);
  const removeOverride = useDeleteFeatureOverride();

  return (
    <Dialog open onOpenChange={open => !open && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {feature.label}{' '}
            <span className="font-mono text-sm text-muted-foreground">{feature.key}</span>
          </DialogTitle>
          <DialogDescription>
            {feature.description} Default: {feature.default_enabled ? 'ON' : 'OFF'}.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label className="mb-2 block">Tenant, role, and user overrides</Label>
            <div className="space-y-1">
              {(data?.overrides ?? []).length === 0 && (
                <p className="text-sm text-muted-foreground">No overrides.</p>
              )}
              {(data?.overrides ?? []).map(o => (
                <div
                  key={o.id}
                  className="flex items-center justify-between rounded border px-3 py-1.5 text-sm"
                >
                  <span className="flex items-center gap-2">
                    <Badge variant="outline">{o.subject_type}</Badge>
                    <span>{o.subject_label ?? `deleted ${o.subject_type}`}</span>
                    <Badge variant={o.enabled ? 'default' : 'secondary'}>
                      {o.enabled ? 'ON' : 'OFF'}
                    </Badge>
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={removeOverride.isPending}
                    onClick={async () => {
                      try {
                        await removeOverride.mutateAsync({
                          key: feature.key,
                          overrideId: o.id,
                        });
                        toast.success('Override removed');
                      } catch (e) {
                        toast.error(e instanceof Error ? e.message : 'Remove failed');
                      }
                    }}
                  >
                    Remove
                  </Button>
                </div>
              ))}
            </div>
          </div>

          <AddOverrideForm featureKey={feature.key} roles={roles} />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function FeaturesPage() {
  const { data, isLoading, error } = useFeatures();
  const setPlatform = useSetPlatformOverride();
  const clearPlatform = useClearPlatformOverride();
  const [detailKey, setDetailKey] = useState<string | null>(null);

  const detail = data?.features.find(f => f.key === detailKey) ?? null;

  const onEnvironmentChange = async (feature: FeatureRegistryEntry, value: EnvironmentState) => {
    try {
      if (value === 'default') {
        await clearPlatform.mutateAsync(feature.key);
      } else {
        await setPlatform.mutateAsync({ key: feature.key, enabled: value === 'on' });
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Update failed');
    }
  };

  return (
    <div>
      <PageHeader
        title="Features"
        description={`Feature flags for ${data?.environment ?? 'this environment'}. Effective value per user: user override > role override > tenant override > legacy tenant setting > environment override > default.`}
      />
      <QueryState isLoading={isLoading} error={error} empty={data?.features.length === 0}>
        <div className="rounded-3xl border overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Feature</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Tags</TableHead>
                <TableHead>Default</TableHead>
                <TableHead>Environment</TableHead>
                <TableHead>Overrides</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data?.features.map(f => (
                <TableRow key={f.key}>
                  <TableCell>
                    <button
                      className="text-left hover:underline"
                      onClick={() => setDetailKey(f.key)}
                    >
                      <span className="font-medium">{f.label}</span>
                      <span className="ml-2 font-mono text-xs text-muted-foreground">{f.key}</span>
                    </button>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline">{f.category}</Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {(f.tags ?? []).map(tag => (
                        <Badge key={tag} variant="secondary">
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant={f.default_enabled ? 'default' : 'secondary'}>
                      {f.default_enabled ? 'ON' : 'OFF'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Select
                      value={environmentState(f)}
                      onValueChange={v => onEnvironmentChange(f, v as EnvironmentState)}
                    >
                      <SelectTrigger className="w-32">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="default">Default</SelectItem>
                        <SelectItem value="on">Force ON</SelectItem>
                        <SelectItem value="off">Force OFF</SelectItem>
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    <button className="hover:underline" onClick={() => setDetailKey(f.key)}>
                      {f.tenant_override_count} tenant · {f.role_override_count ?? 0} role ·{' '}
                      {f.user_override_count} user
                    </button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </QueryState>

      {detail && (
        <FeatureDetailDialog
          feature={detail}
          roles={data?.roles ?? []}
          onClose={() => setDetailKey(null)}
        />
      )}
    </div>
  );
}
